import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, NgForm } from '@angular/forms';

import { DevicesService, ILicenseData } from '@nrwl/common-lib';

import {
  IComponentStateHistory,
  IValueChangedEvent,
  IWizardContentComponent
} from '../../../wizard.content';
import { REMOVED } from '../../../services/base.wizard.step';

@Component({
  selector: 'datasource-agent-desc-html',
  templateUrl: './datasource.agent.desc.html',
  providers: [],
  styleUrls: [
    '../datasource.style.scss',
    './datasource.agent.desc.style.scss',
    '../../../wizard.button.style.scss'
  ]
})
export class DatasourceAgentDescriptionComponent implements IWizardContentComponent, OnInit {
  @Input() step: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() subStep: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() wizardCtrl: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm') form: NgForm;

  licenses: ILicenseData;
  agentTypesSource = [
    {
      id: 'virtual',
      iconName: 'images/agent_virtual.svg',
      label: 'Virtual agent',
      description: 'An agent you deploy as a virtual machine'
    },
    {
      id: 'windows',
      iconName: 'images/agent_windows.svg',
      label: 'Windows agent',
      description: 'Runs as a service on your Windows machine',
      subDescription: 'Supported on Windows 10'
    }
  ];
  selectedAgentType: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  itemSelected = false;
  alias: string;
  licenseType = 'agent';
  dataSourceTypes = [
    {
      id: 'GigaTestAgent',
      name: 'Active test agent',
      iconSrc: 'images/gigatest.svg'
    }
  ];
  dataSourceType: string;
  hide = true;
  isValidAlias = true;
  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly deviceService: DevicesService
  ) {
    this.dataSourceType = 'GigaTestAgent';
  }

  ngOnInit() {
    this.licenses = {
      max: 0,
      inUse: 0
    };
    this.step.form = this.form;
    const selectedType = this.stateHistory(this.componentId, 'type');
    if (selectedType && selectedType.value) {
      // navigated back from later pages
      this.dataSourceType = selectedType.value;
    } else {
      setTimeout(() => {
        // emit initial choice/so that framework can evaluate validity/populate defaults/etc
        this.typeUpdate('datasourceType', 'datasourceType', this.dataSourceTypes[0], true);
        this.typeUpdate('type', 'type', this.dataSourceTypes[0].id, true);
      }, 0);
    }
    this.selectedAgentType = {};
    this.resetOutputFromParent();
  }

  resetOutputFromParent(): void {
    const agentType = this.stateHistory(this.componentId, 'agentTypeInput');
    if (agentType && agentType.value && agentType.value !== REMOVED) {
      this.itemSelected = true;
      this.selectedAgentType = agentType.value;
      this.selectAgentType(this.selectedAgentType);
    }
    const aliasInput = this.stateHistory(this.componentId, 'aliasInput');
    if (aliasInput && aliasInput.value && aliasInput.value !== REMOVED) {
      this.alias = aliasInput.value;
    }
    const platform = this.stateHistory(this.componentId, 'platformChoice');
    if (platform && platform.value && platform.value !== REMOVED) {
      this.step.output.platform = platform.value;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  selectAgentType(item: any): void {
    this.selectedAgentType = item;
    this.itemSelected = true;
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'agentTypeInput',
      key: 'agentType',
      newValue: this.selectedAgentType,
      isValid: true
    });
    this.setPlatform();
  }

  setPlatform(): void {
    if (this.selectedAgentType.id === 'windows') {
      this.step.dataValueChange('platformChoice', 'platform', 'WINDOWS');
    } else if (this.selectedAgentType.id === 'hardware') {
      this.step.dataValueChange('platformChoice', 'platform', 'OLHW');
    }
  }

  onChangeItemSelection(): void {
    this.step.stateOfExtendedControls = {};
    this.alias = '';
    this.selectedAgentType = {};
    this.itemSelected = false;
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'agentTypeInput',
      key: 'agentType',
      newValue: REMOVED,
      isValid: false
    });
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'aliasInput',
      key: 'alias',
      newValue: REMOVED,
      isValid: false
    });
  }

  onChangeAlias(): void {
    const deviceDatas = this.deviceService.getDevices();
    this.isValidAlias = !deviceDatas
      .map(devices => devices.deviceData)
      .some(device => device.name.toLowerCase().trim() === this.alias.toLowerCase().trim());
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'aliasInput',
      key: 'alias',
      newValue: this.alias,
      isValid: this.isValidAlias ? this.form.controls.alias.valid : false
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  selectDatasourceType(item: any): void {
    this.dataSourceType = item.id;
    this.typeUpdate('datasourceType', 'datasourceType', item, true);
    this.typeUpdate('type', 'type', item.id, true);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  typeUpdate(inputVal: any, key: any, newVal: any, isValid: any): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: inputVal,
      key: key,
      newValue: newVal,
      isValid: isValid
    });
  }

  availableLicenseCount(value: ILicenseData): void {
    if (this.licenses) {
      this.licenses.max = value.max;
      this.licenses.inUse = value.inUse;
    }
  }

  isLicenseAvailable(): boolean {
    return this.licenses?.max > this.licenses?.inUse;
  }

  showLicenseMessage(): boolean {
    return this.licenses?.max > 0 && this.licenses?.max === this.licenses?.inUse;
  }
}
